import React, { useMemo } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader } from './ui/card';
import {
  computeMainOeuvreCategoryWeeklyTotals,
  mainOeuvreDurationHours,
  mergeMainOeuvreOpInputs,
  mainOeuvreSlotProduct,
} from './devisQuoteState';

const SLOTS = [
  { key: 'jour', title: 'Intervention de jour' },
  { key: 'nuit', title: 'Intervention de nuit' },
  { key: 'weekend', title: 'Intervention week-end' },
];

const fmtHours = (h) =>
  h == null
    ? '—'
    : new Intl.NumberFormat('fr-FR', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
      }).format(h);

const fmtProduct = (n) =>
  new Intl.NumberFormat('fr-FR', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(Number.isFinite(n) ? n : 0);

/** Heures décimales → affichage type 04:30 */
const fmtHoursClock = (decimalHours) => {
  if (!Number.isFinite(decimalHours) || decimalHours < 0) return '00:00';
  const totalMinutes = Math.round(decimalHours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const DevisQuoteMainOeuvreTab = ({
  operationCategories,
  mainOeuvre,
  theme,
  patchMainOeuvre,
  canEdit,
}) => {
  const inputStyle = {
    borderColor: theme.border,
    backgroundColor: theme.inputBackground,
    color: theme.text,
  };

  const readonlyStyle = {
    ...inputStyle,
    opacity: 0.95,
    cursor: 'default',
  };

  const byOp = mainOeuvre?.inputsByOperationId || {};

  const updateSlot = (opId, slotKey, field, raw) => {
    patchMainOeuvre((prev) => {
      const base = prev.inputsByOperationId || {};
      const merged = mergeMainOeuvreOpInputs(base, opId);
      return {
        ...prev,
        inputsByOperationId: {
          ...base,
          [opId]: {
            ...merged,
            [slotKey]: { ...merged[slotKey], [field]: raw },
          },
        },
      };
    });
  };

  const categoryTotals = useMemo(
    () => computeMainOeuvreCategoryWeeklyTotals(mainOeuvre, operationCategories),
    [mainOeuvre, operationCategories]
  );

  const grandTotal = useMemo(
    () => Object.values(categoryTotals).reduce((a, b) => a + b, 0),
    [categoryTotals]
  );

  const opLineProduct = (op) => {
    const inp = mergeMainOeuvreOpInputs(byOp, op.id);
    let sum = 0;
    for (const { key } of SLOTS) {
      const sl = inp[key];
      sum += mainOeuvreSlotProduct(sl.surface, sl.passages, op.cadence_m2h);
    }
    return sum;
  };

  const totalOpCount = operationCategories.reduce((n, c) => n + c.operations.length, 0);

  if (!operationCategories.length || totalOpCount === 0) {
    return (
      <p
        className="text-sm leading-relaxed rounded-xl border-2 px-4 py-4"
        style={{ borderColor: theme.border, backgroundColor: theme.panelInset, color: theme.textSecondary }}
      >
        Aucune opération dans le référentiel (ou catégories vides). Ajoutez des opérations dans{' '}
        <span className="font-semibold" style={{ color: theme.text }}>
          Paramètres → Opérations
        </span>
        .
      </p>
    );
  }

  const inpH = 'h-11 min-h-[44px] rounded-xl border-2 text-base tabular-nums sm:h-10 sm:text-sm touch-manipulation';

  return (
    <div className="space-y-5 min-w-0 sm:space-y-6">
      <div
        className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 px-4 py-4 shadow-sm"
        style={{ borderColor: theme.accent, backgroundColor: theme.accentSoft }}
      >
        <p className="text-sm font-semibold min-w-0 sm:text-base" style={{ color: theme.text }}>
          Total heures :{' '}
          <span className="text-lg font-bold tabular-nums sm:text-xl" style={{ color: theme.text }}>
            {fmtHoursClock(grandTotal)}
          </span>
        </p>
      </div>

      <p
        className="text-sm leading-relaxed rounded-xl border px-4 py-3"
        style={{ borderColor: theme.border, backgroundColor: theme.panelInset, color: theme.textSecondary }}
      >
        Une ligne par opération. Renseignez jour, nuit et/ou week-end : surface et passages par semaine ;
        la durée quotidienne est calculée à partir de la cadence définie dans les paramètres.
      </p>

      {operationCategories.map((cat) => {
        const catTotal = categoryTotals[cat.id] ?? 0;
        if (!cat.operations.length) return null;

        return (
          <Card
            key={cat.id}
            className="min-w-0 overflow-hidden rounded-2xl border shadow-sm"
            style={{ borderColor: theme.border, backgroundColor: theme.surface }}
          >
            <CardHeader
              className="border-b space-y-1 px-4 py-4"
              style={{ borderColor: theme.border, backgroundColor: theme.panelInset }}
            >
              <p className="text-sm font-semibold sm:text-base" style={{ color: theme.text }}>
                {cat.label || 'Catégorie'}
              </p>
              <p className="text-xs font-medium sm:text-sm" style={{ color: theme.textSecondary }}>
                Total catégorie :{' '}
                <span className="tabular-nums font-semibold" style={{ color: theme.text }}>
                  {fmtHoursClock(catTotal)}
                </span>
              </p>
            </CardHeader>
            <CardContent className="space-y-5 p-4 sm:p-5">
              {cat.operations.map((op) => {
                const inp = mergeMainOeuvreOpInputs(byOp, op.id);
                const lineTotal = opLineProduct(op);
                return (
                  <div
                    key={op.id}
                    className="rounded-xl border-2 p-4 space-y-4"
                    style={{ borderColor: theme.border, backgroundColor: theme.panelInset }}
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="text-sm font-semibold sm:text-base" style={{ color: theme.text }}>
                        {op.label || 'Opération'}
                      </p>
                      <div className="text-right text-xs space-y-0.5 sm:text-sm" style={{ color: theme.textSecondary }}>
                        <p>
                          Cadence :{' '}
                          <span className="tabular-nums font-medium" style={{ color: theme.text }}>
                            {op.cadence_m2h > 0
                              ? `${new Intl.NumberFormat('fr-FR').format(op.cadence_m2h)} m²/h`
                              : '— (à définir en paramètres)'}
                          </span>
                        </p>
                        <p className="tabular-nums">
                          Sous-total ligne : <span style={{ color: theme.text }}>{fmtProduct(lineTotal)}</span>
                        </p>
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {SLOTS.map(({ key, title }) => {
                        const sl = inp[key];
                        const dur = mainOeuvreDurationHours(sl.surface, op.cadence_m2h);
                        return (
                          <div
                            key={key}
                            className="space-y-3 rounded-xl border-2 p-3 sm:p-4"
                            style={{ borderColor: theme.border, backgroundColor: theme.surface }}
                          >
                            <p className="text-xs font-bold uppercase tracking-wide sm:text-sm" style={{ color: theme.text }}>
                              {title}
                            </p>
                            <div className="space-y-2">
                              <div className="space-y-1">
                                <Label className="text-xs font-medium" style={{ color: theme.textSecondary }}>
                                  Surface (m²)
                                </Label>
                                <Input
                                  type="number"
                                  min={0}
                                  step={0.01}
                                  inputMode="decimal"
                                  value={sl.surface === '' ? '' : sl.surface}
                                  onChange={(e) => updateSlot(op.id, key, 'surface', e.target.value)}
                                  disabled={!canEdit}
                                  className={inpH}
                                  style={inputStyle}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs font-medium" style={{ color: theme.textSecondary }}>
                                  Passages / semaine
                                </Label>
                                <Input
                                  type="number"
                                  min={0}
                                  step={0.1}
                                  inputMode="decimal"
                                  value={sl.passages === '' ? '' : sl.passages}
                                  onChange={(e) => updateSlot(op.id, key, 'passages', e.target.value)}
                                  disabled={!canEdit}
                                  className={inpH}
                                  style={inputStyle}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs font-medium" style={{ color: theme.textSecondary }}>
                                  Durée / jour (h)
                                </Label>
                                <Input
                                  readOnly
                                  tabIndex={-1}
                                  value={fmtHours(dur)}
                                  className={`${inpH} opacity-95`}
                                  style={readonlyStyle}
                                  aria-readonly="true"
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default DevisQuoteMainOeuvreTab;
